/**
 * GraderPage — THE DEAN DEMO WOW MOMENT.
 *
 *  ┌─────────────────────┬──────────────────────┬───────────────────────────┐
 *  │   RUBRIC (left)     │ SUBMISSION (middle)  │  AI DRAFTS + EDIT (right) │
 *  │   criteria list     │ student text body    │  per-criterion score +    │
 *  │                     │                      │  feedback + citations     │
 *  │                     │                      │  [✓ Approve all] [Edit]   │
 *  └─────────────────────┴──────────────────────┴───────────────────────────┘
 *
 * One-click "AI Score" runs the entire rubric through Claude in parallel
 * via /api/submissions/{id}/ai-score. Faculty can edit any score/feedback,
 * then "Finalize" commits to the gradebook.
 *
 * Patent claim 22 demo surface.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import {
  getSubmission, getRubric, aiScoreSubmission, finalizeSubmission,
} from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';

const GRADER_ROLES = ['developer', 'institution_admin', 'teacher'];

const GraderPage = () => {
  const { subId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const canGrade = user && GRADER_ROLES.includes(user.role);

  const [submission, setSubmission] = useState(null);
  const [rubric, setRubric] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scoring, setScoring] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [error, setError] = useState('');

  // Per-criterion working state — initialized from AI drafts or final scores.
  // shape: { [criterionId]: { score, feedback } }
  const [edits, setEdits] = useState({});

  const refresh = async () => {
    try {
      const sub = await getSubmission(subId);
      const r = await getRubric(sub.rubric_id);
      setSubmission(sub);
      setRubric(r);
      // Seed edits from final_scores if present, else from ai_drafts, else blank.
      const seed = {};
      const drafts = sub.ai_drafts || [];
      const finals = sub.final_scores || [];
      for (const c of r.criteria) {
        const fin = finals.find((f) => f.criterion_id === c.id);
        const drf = drafts.find((d) => d.criterion_id === c.id);
        seed[c.id] = fin
          ? { score: fin.score, feedback: fin.feedback || '' }
          : drf
            ? { score: drf.score, feedback: drf.feedback || '' }
            : { score: 0, feedback: '' };
      }
      setEdits(seed);
    } catch (e) {
      setError(e.response?.data?.detail || 'Load failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
     
  }, [subId]);

  const runAiScore = async () => {
    setScoring(true);
    setError('');
    try {
      const res = await aiScoreSubmission(subId);
      // Seed edits from the just-drafted AI output.
      const seed = { ...edits };
      for (const d of res.drafts) {
        seed[d.criterion_id] = { score: d.score, feedback: d.feedback };
      }
      setEdits(seed);
      await refresh();
    } catch (e) {
      setError(e.response?.data?.detail || 'AI scoring failed');
    } finally {
      setScoring(false);
    }
  };

  const finalize = async () => {
    setFinalizing(true);
    setError('');
    try {
      const finalScores = Object.entries(edits).map(([cid, v]) => ({
        criterion_id: cid,
        score: Number(v.score) || 0,
        feedback: v.feedback || '',
      }));
      await finalizeSubmission(subId, finalScores);
      await refresh();
    } catch (e) {
      setError(e.response?.data?.detail || 'Finalize failed');
    } finally {
      setFinalizing(false);
    }
  };

  const weightedTotal = useMemo(() => {
    if (!rubric) return 0;
    return rubric.criteria.reduce((sum, c) => {
      const e = edits[c.id] || { score: 0 };
      return sum + (Number(e.score) || 0) * (Number(c.weight) || 0);
    }, 0);
  }, [rubric, edits]);

  if (loading) return (<div className="min-h-screen"><Navbar /><div className="p-8 text-gray-500">Loading…</div></div>);
  if (!submission || !rubric) return (<div className="min-h-screen"><Navbar /><div className="p-8 text-red-600">{error || 'Not found'}</div></div>);

  const driftBy = (cid) => {
    const drf = (submission.ai_drafts || []).find((d) => d.criterion_id === cid);
    if (!drf) return null;
    const cur = Number(edits[cid]?.score) || 0;
    return Math.abs(drf.score - cur) > 0.01 ? (cur - drf.score).toFixed(1) : null;
  };

  return (
    <div className="min-h-screen" data-testid="grader-page">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-2">
          <Link to={`/rubrics/${rubric.id}/submissions`} className="text-sm text-gray-500 hover:underline">
            ← All submissions for {rubric.title}
          </Link>
        </div>
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl font-bold" data-testid="grader-student-name">{submission.student_name}</h1>
            <div className="text-xs text-gray-500">{submission.student_email} · submitted {new Date(submission.submitted_at).toLocaleString()}</div>
          </div>
          {canGrade && (
            <div className="flex gap-2">
              <button
                onClick={runAiScore}
                disabled={scoring}
                className="btn-primary"
                data-testid="ai-score-btn"
                title="Run AI grading against your rubric and course material"
              >
                {scoring ? 'Scoring…' : '🤖 AI Score'}
              </button>
              <button
                onClick={finalize}
                disabled={finalizing}
                className="btn-secondary"
                data-testid="finalize-btn"
              >
                {finalizing ? 'Finalizing…' : '✓ Finalize'}
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm" data-testid="grader-error">{error}</div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* LEFT: Rubric */}
          <section className="card p-4" data-testid="rubric-panel">
            <h2 className="font-bold mb-2">Rubric</h2>
            <div className="text-xs text-gray-500 mb-3">
              Max total <span className="font-mono">{rubric.total_max_points}</span>
            </div>
            <ul className="space-y-3 text-sm">
              {rubric.criteria.map((c) => (
                <li key={c.id} className="border-l-2 pl-2" style={{ borderColor: 'var(--unthsc-green)' }}>
                  <div className="font-semibold">{c.name}</div>
                  <div className="text-xs text-gray-500">Up to {c.max_points} pts · weight ×{c.weight}</div>
                  {c.description && <div className="text-xs text-gray-600 mt-1">{c.description}</div>}
                </li>
              ))}
            </ul>
          </section>

          {/* MIDDLE: Submission */}
          <section className="card p-4" data-testid="submission-panel">
            <h2 className="font-bold mb-2">Submission</h2>
            {submission.file_url && (
              <a href={submission.file_url} className="text-xs text-blue-600 hover:underline mb-2 inline-block" data-testid="submission-file-link">
                📎 attached file
              </a>
            )}
            <pre className="whitespace-pre-wrap text-sm text-gray-800 font-sans" data-testid="submission-text">
              {submission.content_text || '(no text)'}
            </pre>
          </section>

          {/* RIGHT: AI drafts + editable final scores */}
          <section className="card p-4" data-testid="drafts-panel">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-bold">Scoring</h2>
              <div className="text-sm" data-testid="weighted-total-display">
                <span className="text-gray-500">Total:</span>{' '}
                <span className="font-mono font-bold">{weightedTotal.toFixed(2)}</span>
                <span className="text-gray-400"> / {rubric.total_max_points}</span>
              </div>
            </div>

            {(!submission.ai_drafts || submission.ai_drafts.length === 0) && (
              <div className="text-xs text-gray-500 mb-3">
                No AI drafts yet. Click <strong>🤖 AI Score</strong> above to generate them.
              </div>
            )}

            <div className="space-y-4">
              {rubric.criteria.map((c) => {
                const drf = (submission.ai_drafts || []).find((d) => d.criterion_id === c.id);
                const drift = driftBy(c.id);
                return (
                  <div key={c.id} className="border-b border-gray-200 pb-3 last:border-b-0" data-testid={`criterion-block-${c.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-sm">{c.name}</span>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          step="0.5"
                          min={0}
                          max={c.max_points}
                          className="input-field w-16 text-right py-1 text-sm"
                          value={edits[c.id]?.score ?? 0}
                          onChange={(e) =>
                            setEdits({
                              ...edits,
                              [c.id]: { ...(edits[c.id] || {}), score: e.target.value },
                            })
                          }
                          disabled={!canGrade}
                          data-testid={`final-score-input-${c.id}`}
                        />
                        <span className="text-xs text-gray-500">/ {c.max_points}</span>
                        {drift && (
                          <span className="text-[10px] font-mono text-amber-600" title="Faculty changed AI's draft" data-testid={`drift-${c.id}`}>
                            {drift > 0 ? `+${drift}` : drift}
                          </span>
                        )}
                      </div>
                    </div>
                    <textarea
                      rows={3}
                      className="input-field text-sm"
                      value={edits[c.id]?.feedback ?? ''}
                      onChange={(e) =>
                        setEdits({
                          ...edits,
                          [c.id]: { ...(edits[c.id] || {}), feedback: e.target.value },
                        })
                      }
                      placeholder="Feedback to the student"
                      disabled={!canGrade}
                      data-testid={`final-feedback-input-${c.id}`}
                    />
                    {drf?.citations?.length > 0 && (
                      <div className="mt-1 text-xs" data-testid={`citations-${c.id}`}>
                        <span className="text-gray-500">AI cited: </span>
                        {drf.citations.map((cite, i) => (
                          <span key={i} className="inline-block bg-emerald-50 border border-emerald-200 text-emerald-800 px-1.5 py-0.5 rounded mr-1 mb-1 font-mono">
                            {cite.length > 60 ? cite.slice(0, 57) + '…' : cite}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {submission.finalized_at && (
              <div className="mt-4 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-800" data-testid="finalized-banner">
                ✓ Finalized by {submission.finalized_by} on {new Date(submission.finalized_at).toLocaleString()} ·
                Score: <span className="font-bold">{submission.final_total} / {rubric.total_max_points}</span>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
};

export default GraderPage;
